import type { Accessor, ContextProviderComponent, Resource } from "solid-js";
import { createContext, createMemo, createResource, createSignal, onCleanup } from "solid-js";
import type { User } from "oidc-client-ts";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { AUTH_APP_ID, AUTH_BASE_URL } from "../constants";
import { isNil } from "lodash-es";
import { initializedContext } from "../utils/initializedUtil";
import type CustomerId from "./CustomerId";
import logger from "../utils/logger";

export type LoginStatus = "loggedIn" | "loading" | "loggedOut";

export type Impersonation = { type: "customer"; value: CustomerId } | { type: "user"; value: string };

type State = {
  loginStatus: Accessor<LoginStatus>;
  user: Resource<User | null>;
  impersonation: Accessor<Impersonation | undefined>;
};

type LoginOptions = {
  /** Shows the login form even when the identity server still has a session, which would otherwise log that account in */
  forcePrompt?: boolean;
};

type Dispatch = {
  login: (origin?: string, options?: LoginOptions) => Promise<void>;
  logout: (origin?: string) => Promise<void>;
  handleLoginCallback: () => Promise<string>;
  handleLogoutCallback: () => Promise<string>;
  refreshAccessToken: () => Promise<string>;
  getCurrentUser: () => Promise<User | null>;
  impersonateUser: (userId: string) => void;
  impersonateCustomer: (customerId: CustomerId) => void;
  stopImpersonation: () => void;
};

const RESPONSE_TYPE = "code";
// Prefix of the key under which oidc-client-ts stores the session in the `userStore`
const USER_STORAGE_KEY_PREFIX = "oidc.user:";
const SCOPE = "openid offline";

const StateContext = createContext<State>();
const DispatchContext = createContext<Dispatch>();

export const AuthProvider: ContextProviderComponent<undefined> = (props) => {
  const userManager = new UserManager({
    authority: AUTH_BASE_URL,
    client_id: AUTH_APP_ID,
    redirect_uri: `${window.location.origin}/login/callback`,
    post_logout_redirect_uri: `${window.location.origin}/logout/callback`,
    response_type: RESPONSE_TYPE,
    scope: SCOPE,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });

  const [user, { mutate }] = createResource(() => userManager.getUser());
  const loginStatus = createMemo<LoginStatus>(() => {
    if (user.loading) {
      return "loading";
    }
    return isNil(user.latest) ? "loggedOut" : "loggedIn";
  });
  const [impersonation, setImpersonation] = createSignal<Impersonation>();

  // Tabs share the stored session, so after logging in as another account (or out) in one tab, the others would send
  // that account's token while still showing the previous account's data. Token refreshes keep the account, so these
  // are ignored.
  const reloadOnAccountChangeInOtherTab = async (event: StorageEvent) => {
    if (!event.key?.startsWith(USER_STORAGE_KEY_PREFIX) || user.loading) {
      return;
    }
    const storedUser = await userManager.getUser();
    if (storedUser?.profile.sub !== user.latest?.profile.sub) {
      window.location.reload();
    }
  };
  window.addEventListener("storage", reloadOnAccountChangeInOtherTab);
  onCleanup(() => window.removeEventListener("storage", reloadOnAccountChangeInOtherTab));

  const login = async (origin?: string, options?: LoginOptions) => {
    // The login callback page would otherwise start as the stored (possibly other) account and fire requests with it,
    // e.g. caching it as the current user, before the new session is stored
    await userManager.removeUser();
    // Keeps the app in line with the removed session when the redirect fails, or the page is restored with 'back'
    mutate(null);
    await userManager.signinRedirect({ state: origin, ...(options?.forcePrompt ? { prompt: "login" } : {}) });
  };

  const logout = async (origin?: string) => {
    const idToken = user.latest?.id_token;
    await userManager.removeUser();
    await userManager.signoutRedirect({ id_token_hint: idToken, state: origin });
    mutate(null);
  };

  const handleLoginCallback = async () => {
    try {
      const user = await userManager.signinCallback();
      mutate(user);

      return !isNil(user) ? (user?.state as string) : "/";
    } catch (e) {
      await userManager.removeUser();
      await userManager.clearStaleState();
      throw e;
    }
  };

  const handleLogoutCallback = async () => {
    const signoutResponse = await userManager.signoutRedirectCallback();
    return !isNil(signoutResponse.userState) ? (signoutResponse.userState as string) : "/";
  };

  const refreshAccessToken = async (): Promise<string> =>
    await navigator.locks.request("moreapp_platform_access_token_refresh", async () => {
      logger.debug("Refreshing access token");

      try {
        const user = await userManager.signinSilent();
        if (isNil(user)) {
          await logout();
          return Promise.reject("Failed to refresh access token");
        }

        mutate(user);
        return user.access_token;
      } catch (e) {
        await logout();
        throw e;
      }
    });

  const getCurrentUser = async () => userManager.getUser();

  const impersonateUser = (userId: string) => setImpersonation({ type: "user", value: userId });

  const impersonateCustomer = (customerId: CustomerId) => setImpersonation({ type: "customer", value: customerId });

  const stopImpersonation = () => {
    setImpersonation(undefined);
    window.location.href = "/"; // Forces a browser reload, and thus also clearing local cache
  };

  const state = {
    loginStatus,
    user,
    impersonation,
  };

  const dispatch: Dispatch = {
    login,
    logout,
    handleLoginCallback,
    handleLogoutCallback,
    refreshAccessToken,
    getCurrentUser,
    impersonateUser,
    impersonateCustomer,
    stopImpersonation,
  };

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{props.children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export const useAuthState = () => initializedContext(StateContext, "useAuthState", "AuthProvider");
export const useAuthDispatch = () => initializedContext(DispatchContext, "useAuthDispatch", "AuthProvider");
